import React, { forwardRef, useId, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { useTranslation } from 'react-i18next';
import TagEditor from '../Widgets/TagEditor';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { getPathRootLabel, presentActionPath, resolveActionPathEdit } from '../../Utils/actionPathPresentation';

const ActionPathEditor = forwardRef(({ value = '', onChange, caseid, onBrowse, browseLabel,
  browseDisabled = false, placeholder, ...props }, ref) => {
  const { t } = useTranslation();
  const locationId = useId();
  const clearedLocationRef = useRef(null);
  const { pathRoots = [], allowedRoots = [], displayRoot, loading, error, retry } = useFolderRestrictions(caseid);
  const roots = pathRoots.length ? pathRoots : [
    ...(displayRoot || allowedRoots[0] ? [{ path: displayRoot || allowedRoots[0], kind: 'storage' }] : []),
    ...allowedRoots.slice(1).map((path) => ({ path, kind: 'shared', name: path.split(/[/\\]/).pop() })),
  ];
  if (clearedLocationRef.current && (clearedLocationRef.current.caseid !== caseid
      || (value && value !== clearedLocationRef.current.value))) clearedLocationRef.current = null;
  const clearedLocation = !value && clearedLocationRef.current?.caseid === caseid
    && (!clearedLocationRef.current.presentation.root || roots.some((root) =>
      root.path === clearedLocationRef.current.presentation.root.path)) ? clearedLocationRef.current.presentation : null;
  const presentation = clearedLocation || presentActionPath(value, roots);
  const handleChange = (next) => {
    clearedLocationRef.current = !next ? { caseid, value, presentation: { ...presentation, name: '' } } : null;
    const relativeName = !/[/\\]/.test(next);
    const root = relativeName || !presentation.root
      ? presentation.editRoot || presentation.root?.path : presentation.root.path;
    onChange?.(resolveActionPathEdit(next, root, roots, { relativeName: relativeName && Boolean(root) }));
  };
  const location = presentation.root ? getPathRootLabel(presentation.root, roots, t)
    : t(`workflow.paths.${loading ? 'loading_location' : error ? 'location_unavailable' : presentation.kind}`);
  const safePlaceholder = /^[A-Za-z]:[/\\]|^[/\\]/.test(placeholder || '')
    ? presentActionPath(placeholder, roots).name : placeholder;
  const showLocation = Boolean(value || clearedLocation || error);
  return <Box sx={{ minWidth: 0, width: '100%' }}>
    {showLocation && <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      mb: 0.75,
      px: 1.25,
      py: 0.75,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'action.hover',
    }}>
      <FolderOpenOutlinedIcon fontSize="small" color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
      <Box id={locationId} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, lineHeight: 1.35 }}>
          {t('workflow.paths.location_label')}
        </Typography>
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {[location, presentation.parent].filter(Boolean).join(' › ')}
        </Typography>
      </Box>
      {error && retry && <Button size="small" onClick={retry} sx={{ flexShrink: 0, py: 0, textTransform: 'none' }}>
        {t('workflow.paths.retry_location')}
      </Button>}
    </Box>}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TagEditor {...props} ref={ref} caseid={caseid} value={presentation.name}
          aria-describedby={[props['aria-describedby'], showLocation ? locationId : null].filter(Boolean).join(' ') || undefined}
          placeholder={safePlaceholder || t('workflow.paths.name_placeholder')}
          onChange={handleChange} />
      </Box>
      {onBrowse && <Button variant="outlined" onClick={onBrowse} disabled={props.disabled || browseDisabled}
        sx={{ flexShrink: 0, textTransform: 'none' }}>
        {browseLabel || t('common.browse', 'Browse')}
      </Button>}
    </Box>
  </Box>;
});

export default ActionPathEditor;
